import React, { useRef, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import WebFont from 'webfontloader';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import { dialogs, i18next, view, customization, bowser, i18n } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import fontsData from 'src/assets/fonts.json';
import upsell from 'src/js/modules/upsell';
import segment from 'src/js/modules/analytics/segment';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import FontsSettingsDialog from '../components/fonts-settings-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import helpers from '../helpers';
import constants from '../constants';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import triggerIds from '../constants/trigger-ids';
import { setFontOptionVisible } from '../helpers/font-options-visibility-manager';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import getDefaultElementsStylesValues from '../helpers/get-default-elements-styles-values';
import getBasicLineHeightValues from '../helpers/get-basic-line-height-values';
import {
  elementTypeIds,
  fontStyleIdsMap,
  elementStylesMap,
  optionIdsMap,
} from '../constants/element-customization';
import { AVAILABLE_SURFACES } from '../../blocks/constants/common';

const {
  hostingPackageFeatureNames: { ADVANCED_WEBSITE_CUSTOMIZATION },
} = subscriptionManagerConstants;

const { useForm } = reactHookForm;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  generateUniqueFontPairId,
  linkFont,
  getFontFallbackByCategory,
  prepareFontVariantsString,
  getCompiledFontPairConfig,
} = helpers;
const { DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y } = constants;
const SELECT_CLASS_NAME = 'ws-ui-base-select';

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Fonts')],
  title: i18next.t('Edit your font pair'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t("Changing a font updates it wherever it's used on your website"),
  searchPlaceholder: i18next.t('Search...'),
  searchEmptyTitle: i18next.t('Sorry!'),
  searchEmptyText: i18next.t('Nothing was found'),
  elements: {
    heading1: i18next.t('Heading {number}', { number: 1 }),
    heading2: i18next.t('Heading {number}', { number: 2 }),
    heading3: i18next.t('Heading {number}', { number: 3 }),
    heading4: i18next.t('Heading {number}', { number: 4 }),
    heading5: i18next.t('Heading {number}', { number: 5 }),
    heading6: i18next.t('Heading {number}', { number: 6 }),
    paragraph: i18next.t('Paragraph'),
    blockquote: i18next.t('Quote'),
    button: i18next.t('Buttons'),
    navigation: i18next.t('Navigations'),
    headerPhone: `${i18next.t('Header')} → ${i18next.t('Phone number')}`,
    siteName: i18next.t('Site name'),
    label: `${i18next.t('Form')} → ${i18next.t('label')}`,
    input: `${i18next.t('Form')} → ${i18next.t('input')}`,
  },
  settings: {
    font: i18next.t('Font'),
    weight: i18next.t('Weight'),
    size: i18next.t('Size'),
    height: i18next.t('Height'),
    color: i18next.t('Color'),
    capitalization: i18next.t('Capitalization'),
    letterSpacing: i18next.t('Letter spacing'),
  },
  manageColorsButton: i18next.t('Manage colors'),
  fontSizeNotification: i18next.t(
    'Note: to ensure great accessibility font sizes over 64 are scaled down on tablets and mobile.'
  ),
  noResultsText: i18next.t('No result'),
});

const getAccordionSections = (captions) =>
  Object.values(elementTypeIds).map((id) => ({ id, title: captions[id] }));

const getFontPairConfig = (fontPair) => {
  if (fontPair.isCustom) {
    const config = { ...fontPair };

    if (!fontPair.basicLineHeightValues) {
      config.basicLineHeightValues = getBasicLineHeightValues(fontPair.variables);
    }

    return config;
  }

  return {
    ...fontPair,
    id: generateUniqueFontPairId(fontPair.id),
    parentId: fontPair.id,
    isCustom: true,
    basicLineHeightValues: getBasicLineHeightValues(fontPair.variables),
  };
};

const preloadFont = (font) => {
  const liveDocument = view.accessors.getLiveDocument();
  const config = {
    google: {
      families: [font],
    },
    classes: false,
    context: liveDocument.defaultView,
  };

  WebFont.load(config);
};

const FontsSettingsDialogContainer = ({
  fontPairId,
  initialActiveSection = elementTypeIds.HEADING_1,
  sourceId,
  scrollTop = null,
  headerTextContentVisibility = true,
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const {
    activeFontPairId,
    customFonts,
    allFonts,
    applyCustomFontPair,
    activeSections: sharedActiveSections = null,
    modifiedValuesMap: modifiedValues = null,
    offsetX = DEFAULT_OFFSET_X,
    offsetY = DEFAULT_OFFSET_Y,
  } = getSharedData();

  const {
    control,
    reset,
    getFieldState,
    handleSubmit: manageSubmit,
    getValues,
    setValue,
    formState,
  } = useForm();

  const captions = getCaptions();
  const accordionSections = getAccordionSections(captions.elements);
  const limits = getCurrentPackageFeatureLimits();
  const isAdvancedFontCustomizationAvailable = limits.advancedWebsiteCustomization.available;
  const fontPair = allFonts.find(({ id }) => id === fontPairId);
  const currentColorPalette = useSelector(customization.accessors.getCurrentColorPalette);
  const locale = useSelector(i18n.selectors.getLocale);

  const selectedFontPair = fontPair.isCustom ? getCompiledFontPairConfig(fontPair) : fontPair;
  const fontPairConfig = useMemo(() => getFontPairConfig(selectedFontPair), [selectedFontPair]);

  const fontPairVariables = useRef(selectedFontPair.variables);
  const modifiedVariables = useRef([]);
  const preloadedFonts = useRef({});
  const contentRef = useRef(null);

  const [isDevMode] = useState(() => isDeveloperMode());
  const [activeSections, setActiveSections] = useState(
    sharedActiveSections || [initialActiveSection]
  );
  const [dialogPosition, setDialogPosition] = useState({
    offsetX,
    offsetY,
  });
  const [filteredAccordionSections, setFilteredAccordionSections] = useState(accordionSections);
  const [isVisibleHeaderTextContent, setIsVisibleHeaderTextContent] = useState(
    headerTextContentVisibility
  );

  const updateSharedState = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      sourceId,
      ...customData,
    });
  };

  const handleSearchQueryChange = (query) => {
    if (query.trim()) {
      setFilteredAccordionSections(
        accordionSections.filter(({ title }) => title.toLowerCase().includes(query.toLowerCase()))
      );

      trackEvent(events.DESIGN_SETTINGS_SEARCH_PERFORMED, {
        sourceId: triggerIds.EDIT_FONT_PAIR,
        searchTerm: query,
      });
    }
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setIsVisibleHeaderTextContent(true);
  };

  const handleSearchQueryClear = () => {
    setFilteredAccordionSections(accordionSections);
    contentRef.current?.scrollTo({ top: 0 });
  };

  const handleSearchFocus = () => {
    trackEvent(events.DESIGN_SETTINGS_SEARCH_CLICKED, {
      sourceId: triggerIds.EDIT_FONT_PAIR,
    });
  };

  const handleSubmit = (data, event) => {
    if (isAdvancedFontCustomizationAvailable || isDevMode) {
      const dirtyFields = Object.keys(data).filter((key) => getFieldState(key).isDirty);
      const fontPairAdjusted = !!dirtyFields.length;

      modifiedVariables.current = Array.from(
        new Set([...modifiedVariables.current, ...dirtyFields])
      );

      const { primaryFontFamily, secondaryFontFamily } = fontPairVariables.current;

      const newUsedFontFamilies = modifiedVariables.current
        .filter((variable) => variable.endsWith(elementStylesMap.FONT_FAMILY))
        .map((variable) =>
          fontPairVariables.current[variable].split(',')[0].replace(/"/g, '').trim()
        )
        .filter(
          (family) =>
            // we need to filter out font families that start with "var" as we can have some overrides
            // as `var(--primary-font-family)` or `var(--secondary-font-family)` after migration
            !family.startsWith('var') &&
            family !== primaryFontFamily &&
            family !== secondaryFontFamily
        );

      let { usedFonts } = fontPairVariables.current;

      if (newUsedFontFamilies.length) {
        const newUsedFontsString = Array.from(new Set(newUsedFontFamilies))
          .map((fontName) => {
            const { family, variants } = fontsData.find((font) => font.family === fontName);
            return `${family.replace(/ /g, '+')}:${prepareFontVariantsString(variants)}`;
          })
          .join(' | ');

        const usedFontsSet = Array.from(
          new Set(`${usedFonts} | ${newUsedFontsString}`.split(' | '))
        );

        usedFonts = usedFontsSet
          .filter((font) => {
            const family = font.split(':')[0].replace(/\+/g, ' ');
            return (
              family === primaryFontFamily ||
              family === secondaryFontFamily ||
              newUsedFontFamilies.includes(family)
            );
          })
          .join(' | ');
      }

      const variableOverrides = modifiedVariables.current.reduce((acc, variable) => {
        acc[variable] = fontPairVariables.current[variable];
        return acc;
      }, {});

      const { variables, ...restConfig } = fontPairConfig;

      const newConfig = {
        ...restConfig,
        variableOverrides: { ...variableOverrides, usedFonts },
      };

      applyCustomFontPair({ fontPairConfig: newConfig });

      const filteredCustomFonts = customFonts.filter(
        (customFont) => customFont.id !== fontPairConfig.id
      );

      updateSharedState({
        customFonts: [...filteredCustomFonts, newConfig],
        activeFontPairId: fontPairConfig.id,
        fontPairAdjusted,
        activeSections: null,
        modifiedValuesMap: null,
        searchQuery: '',
      });

      onDialogMainAction({
        triggerId: event ? SUBMIT_BUTTON_TRIGGER_ID : DIALOG_BACKGROUND_TRIGGER_ID,
        sourceId,
        fontPairAdjusted,
      });
    } else {
      applyCustomFontPair({ fontPairId: activeFontPairId });
    }

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
      sourceId: dialogCallSourceIds.EDIT_FONT_PAIR,
    });
  };

  const handleCancel = () => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      activeSections: null,
      modifiedValuesMap: null,
      searchQuery: '',
    });

    if (Object.keys(formState.dirtyFields).length) {
      applyCustomFontPair({ fontPairId: activeFontPairId });
    }

    onDialogCancel();

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
      sourceId: dialogCallSourceIds.EDIT_FONT_PAIR,
    });
  };

  const handleFeatureAvailability = (
    elementTypeId,
    elementStyle,
    callback = Function.prototype
  ) => {
    trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_OPTION_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG,
      optionId: optionIdsMap[elementStyle],
      fontStyleId: fontStyleIdsMap[elementTypeId],
    });

    if (isDevMode) {
      callback();
    }

    upsell.operations
      .requestFeatureAvailability(ADVANCED_WEBSITE_CUSTOMIZATION)
      .then(callback)
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
        document.activeElement.blur();
      });
  };

  const onManageColorsClick = (elementTypeId) => {
    const values = getValues();
    const modifiedValuesMap = Object.keys(formState.dirtyFields).reduce((acc, fieldName) => {
      if (fieldName.endsWith(elementStylesMap.LINE_HEIGHT) && values[fieldName] === 'Auto') {
        acc[fieldName] = fontPairConfig.basicLineHeightValues[fieldName];
        return acc;
      }

      acc[fieldName] = values[fieldName];
      return acc;
    }, {});

    let searchQuery;

    switch (elementTypeId) {
      // This is a temporary solution to set correct search query for buttons and navigation sections in the colors dialog
      // until we add a separate font variables for the header and footer navigations, primary and secondary buttons
      case elementTypeIds.BUTTON:
        if (locale === 'en' || locale === 'en-us') {
          searchQuery = i18next.t('Button');
        } else {
          searchQuery = '';
        }

        break;
      case elementTypeIds.NAVIGATION:
        if (locale === 'en' || locale === 'en-us') {
          searchQuery = i18next.t('Navigation');
        } else {
          searchQuery = '';
        }

        break;
      case elementTypeIds.PHONE_NUMBER:
        searchQuery = i18next.t('Phone number');
        break;
      case elementTypeIds.FORM_LABEL:
        searchQuery = i18next.t('Input label');
        break;
      case elementTypeIds.FORM_INPUT:
        searchQuery = i18next.t('Input field text');
        break;
      default:
        searchQuery = accordionSections.find(({ id }) => id === elementTypeId).title;
    }

    updateSharedState({
      activeSections,
      fontPairId,
      modifiedValuesMap,
      originPalette: currentColorPalette,
      searchQuery,
      isDeveloperMode: isDevMode,
    });

    trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_OPTION_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG,
      optionId: optionIdsMap.fontColor,
      fontStyleId: fontStyleIdsMap[elementTypeId],
    });

    dialogs.operations.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
      triggerId: triggerIds.EDIT_FONT_PAIR,
      colorSchemeId: AVAILABLE_SURFACES[0],
      scrollTop: contentRef.current.scrollTop,
      headerTextContentVisibility: isVisibleHeaderTextContent,
    });
  };

  const handleFontFamilyChange = (elementTypeId, selectedFont) => {
    const fontVariable = `${elementTypeId}${elementStylesMap.FONT_FAMILY}`;
    const weightVariable = `${elementTypeId}${elementStylesMap.FONT_WEIGHT}`;

    const { category, variants } = fontsData.find(({ family }) => family === selectedFont);

    const selectedFontFallback = getFontFallbackByCategory(category);
    const elementFontValue = `"${selectedFont}", ${selectedFontFallback}`;

    const elementFontWeight = getValues(weightVariable);
    let newFontWeight;

    if (variants.includes(elementFontWeight)) {
      newFontWeight = elementFontWeight;
    } else if (variants.includes('400')) {
      newFontWeight = '400';
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    } else if (variants.includes('regular')) {
      newFontWeight = 'regular';
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    } else {
      [newFontWeight] = variants;
      setValue(weightVariable, newFontWeight, { shouldDirty: true });
    }

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [fontVariable]: elementFontValue,
      [weightVariable]: newFontWeight,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleFontWeightChange = (weightVariable, selectedWeight) => {
    fontPairVariables.current = {
      ...fontPairVariables.current,
      [weightVariable]: selectedWeight,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleFontSizeChange = (sizeVariable, selectedSize) => {
    const sizeValue = sizeVariable.startsWith('heading') ? selectedSize : `${selectedSize}px`;

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [sizeVariable]: sizeValue,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleLineHeightChange = (heightVariable, selectedHeight) => {
    if (!selectedHeight) {
      return;
    }

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [heightVariable]: `${selectedHeight}em`,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleTextTransformChange = (
    elementTypeId,
    capitalizationVariable,
    selectedCapitalizationValue
  ) => {
    const callback = () => {
      setValue(capitalizationVariable, selectedCapitalizationValue, { shouldDirty: true });

      fontPairVariables.current = {
        ...fontPairVariables.current,
        [capitalizationVariable]: selectedCapitalizationValue,
      };

      applyCustomFontPair({
        fontPairConfig: {
          ...fontPairConfig,
          variables: fontPairVariables.current,
        },
      });
    };

    handleFeatureAvailability(elementTypeId, elementStylesMap.TEXT_TRANSFORM, callback);
  };

  const handleLetterSpacingChange = (letterSpacingVariable, selectedLetterSpacing) => {
    const letterSpacingValue = String(selectedLetterSpacing).endsWith('em')
      ? selectedLetterSpacing
      : `${selectedLetterSpacing}em`;

    fontPairVariables.current = {
      ...fontPairVariables.current,
      [letterSpacingVariable]: letterSpacingValue,
    };

    applyCustomFontPair({
      fontPairConfig: {
        ...fontPairConfig,
        variables: fontPairVariables.current,
      },
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const observer = useMemo(() => {
    const root = document.querySelector(`.${SELECT_CLASS_NAME}`);

    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            const fontFamily = target.id;
            setFontOptionVisible(fontFamily);
            const font = fontsData.find(({ family }) => family === fontFamily);
            linkFont(font.family);

            if (!preloadedFonts.current[fontFamily]) {
              const fontToPreload = `${font.family.replace(/ /g, '+')}:${font.variants.join(',')}`;
              preloadFont(fontToPreload);
              preloadedFonts.current[fontFamily] = true;
            }

            obs.unobserve(target);
          }
        });
      },
      {
        root,
        rootMargin: '1000px',
        scrollMargin: '1000px',
      }
    );
  }, []);

  useEffect(() => () => observer.disconnect(), [observer]);

  useEffect(() => {
    const timeout = bowser.safari ? 100 : 0;

    if (scrollTop && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollTo(0, scrollTop);
      }, timeout);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [contentRef.current]);

  useEffect(() => {
    const element = contentRef?.current;

    if (!element) return;

    const scrollHandler = () => {
      const scrollBottom = element.scrollHeight - element.clientHeight;
      const isAtBottom = element.scrollTop >= scrollBottom - 1;

      if (activeSections.length < 1) {
        setIsVisibleHeaderTextContent(true);

        return;
      }

      if (isAtBottom) return; // Skip if at bottom to avoid flicker

      if (element.scrollTop > 150) {
        setIsVisibleHeaderTextContent(false);
      } else if (element.scrollTop < 50) {
        setIsVisibleHeaderTextContent(true);
      }
    };

    element.addEventListener('scroll', scrollHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('scroll', scrollHandler);
    };
  }, [activeSections]);

  useLayoutEffect(() => {
    if (!modifiedValues) {
      applyCustomFontPair({ fontPairId });
    }

    if (selectedFontPair.variableOverrides) {
      modifiedVariables.current = Object.keys(selectedFontPair.variableOverrides);
    }

    const defaultValues = getDefaultElementsStylesValues(
      selectedFontPair.variables,
      modifiedVariables.current
    );

    reset(defaultValues);

    if (modifiedValues) {
      Object.entries(modifiedValues).forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: true });

        if (key.endsWith(elementStylesMap.FONT_FAMILY)) {
          const { category } = fontsData.find(({ family }) => family === value);
          const fontFallback = getFontFallbackByCategory(category);
          const elementFontValue = `"${value}", ${fontFallback}`;
          modifiedValues[key] = elementFontValue;
        }

        if (key.endsWith(elementStylesMap.LETTER_SPACING)) {
          const letterSpacingValue = String(value).endsWith('em') ? value : `${value}em`;
          modifiedValues[key] = letterSpacingValue;
        }
      });

      fontPairVariables.current = {
        ...fontPairVariables.current,
        ...modifiedValues,
      };

      applyCustomFontPair({
        fontPairConfig: {
          ...fontPairConfig,
          variables: fontPairVariables.current,
        },
      });
    }

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <FontsSettingsDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      control={control}
      fontObserver={observer}
      contentRef={contentRef}
      activeAccordionItems={activeSections}
      accordionSections={filteredAccordionSections}
      isAdvancedFontCustomizationAvailable={isAdvancedFontCustomizationAvailable}
      basicLineHeightValues={fontPairConfig.basicLineHeightValues}
      onToggleAccordionItem={setActiveSections}
      onSubmit={manageSubmit(handleSubmit)}
      onCancel={handleCancel}
      onDragEnd={handleDragEnd}
      handleFeatureAvailability={handleFeatureAvailability}
      handlers={{
        handleFontFamilyChange,
        handleFontWeightChange,
        handleFontSizeChange,
        handleLineHeightChange,
        handleTextTransformChange,
        handleLetterSpacingChange,
      }}
      onManageColorsClick={onManageColorsClick}
      handleSearchQueryChange={handleSearchQueryChange}
      handleSearchQueryClear={handleSearchQueryClear}
      handleSearchFocus={handleSearchFocus}
    />
  );
};

FontsSettingsDialogContainer.propTypes = {
  fontPairId: PropTypes.string.isRequired,
  initialActiveSection: PropTypes.string,
  sourceId: PropTypes.string.isRequired,
  scrollTop: PropTypes.number,
  headerTextContentVisibility: PropTypes.bool,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default FontsSettingsDialogContainer;
